import React, { useEffect, useMemo, useRef, useState } from 'react';
import { gsap } from 'gsap';
import useMediaQuery from '../../hooks/useMediaQuery';

export interface IBaseTableData {
  headers: IBaseTableHeader[];
  rows: IBaseTableRow[];
}

export interface IBaseTableHeader {
  id: number;
  headerData: JSX.Element;
  hideOnMobile?: boolean;
  columnId: number;
  maxWidth?: number;
}

export interface IBaseTableRow {
  id: number;
  columns: IBaseTableColumn[];
  expandedContent?: JSX.Element;
  expandedAtStart: boolean;
  data?: any;
  expandedContentMobileOnly?: boolean;
}

export interface IBaseTableColumn {
  id: number;
  columnData: JSX.Element;
  filterValue: any;
  hideOnMobile?: boolean;
  maxWidth?: number;
}

export interface IBaseTableProps {
  data: IBaseTableData;
  expandable?: boolean;
  onExpanded?: Function;
  onClosed?: Function;
  hideLoader?: boolean;
  defaultFilter?: IFilteredColumn;
}

export interface ITableRow {
  row: IBaseTableRow;
  numberOfColumns: number;
  onExpanded: Function;
  onClosed: Function;
}

export interface IFilteredColumn {
  id: number;
  descending: boolean;
}

export const TableRow = ({ row, numberOfColumns, onExpanded, onClosed }: ITableRow) => {
  const [expanded, setExpanded] = useState(row.expandedAtStart);
  const [expandedHeight, setExpandedHeight] = useState(0);
  const ref = useRef<HTMLDivElement | null>(null);
  const columnWidth = 100 / numberOfColumns;
  const [childResizeObserver, setChildResizeObserver] = useState<ResizeObserver | null>(null);
  const [expandedContentHeight, setExpandedContentHeight] = useState(0);
  const [expanding, setExpanding] = useState(false);

  const expandRow = () => {
    let ctx = gsap.context(() => {
      const expandedChildren: HTMLDivElement | null = ref.current?.querySelector('.expanded-children') || null;

      if (expanded) {
        if (onClosed) {
          onClosed();
        }
        setExpanded(false);
        setExpanding(false);

        if (childResizeObserver && expandedChildren) {
          childResizeObserver?.unobserve(expandedChildren);
        }

        gsap.to('.expanded-children', {
          duration: 0.3,
          autoAlpha: 0,
          onComplete: () => {
            gsap.to('.base-table-expanded-content', { height: 0, duration: 0.3 });
          },
        });
      } else {
        if (expandedChildren) {
          if (onExpanded) {
            onExpanded();
          }
          setExpanded(true);
          setExpanding(true);

          if (childResizeObserver) {
            childResizeObserver?.observe(expandedChildren);
          }
          gsap.to('.base-table-expanded-content', {
            height: expandedChildren.clientHeight,
            duration: 0.3,
            onComplete: () => {
              gsap.to('.expanded-children', { duration: 0.3, autoAlpha: 1 });
              setExpanding(false);
            },
          });
        }
      }
    }, ref);
  };

  useEffect(() => {
    setChildResizeObserver(
      new ResizeObserver((entries) => {
        setExpandedContentHeight(entries[0].target.clientHeight);
      })
    );
    return () => {
      const expandedChildren: HTMLDivElement | null = ref.current?.querySelector('.expanded-children') || null;
      if (expandedChildren && childResizeObserver) {
        childResizeObserver.unobserve(expandedChildren);
        setChildResizeObserver(null);
      }
    };
  }, []);

  useEffect(() => {
    const expandedContent: HTMLDivElement | null = ref.current?.querySelector('.base-table-expanded-content') || null;
    if (expandedContent && !expanding) {
      expandedContent.style.height = `${expandedContentHeight}px`;
    }
  }, [expandedContentHeight]);

  return (
    <div ref={ref} className="base-table-row">
      <div className="base-table-row-top">
        <div className="base-table-row-columns">
          {row.columns.map((column: IBaseTableColumn) => (
            <div
              id={`column-${column.id}`}
              key={`column-${column.id}`}
              className={`base-table-row-column ${column.hideOnMobile ? 'no-mobile' : ''}`}
              style={{ width: `${columnWidth}%`, maxWidth: column.maxWidth ? `${column.maxWidth}px` : '' }}
            >
              <div tabIndex={0} style={{ display: 'inline-block' }}>
                {column.columnData}{' '}
              </div>
            </div>
          ))}
        </div>
        {row.expandedContent ? (
          <button
            title="expand row"
            type="button"
            tabIndex={0}
            className={`chevron expand-button ${expanded ? 'expanded' : ''} ${
              row.expandedContentMobileOnly ? 'expand-only-mobile' : ''
            } `}
            onClick={expandRow}
            aria-expanded={expanded}
          ></button>
        ) : null}
      </div>
      <div
        className={`base-table-expanded-content ${expanded ? 'expanded' : ''} ${
          row.expandedContentMobileOnly ? 'expand-only-mobile' : ''
        }`}
      >
        <div className="expanded-children">{row.expandedContent}</div>
      </div>
    </div>
  );
};

const BaseTable = ({ data, expandable = false, onExpanded, onClosed, hideLoader, defaultFilter }: IBaseTableProps) => {
  const ref = useRef(null);
  const isMobile = useMediaQuery(576);
  const [filteredRows, setFilteredRows] = useState<IBaseTableRow[]>([]);
  const [filteredColumn, setFilteredColumn] = useState<IFilteredColumn | undefined>();

  const rows: IBaseTableRow[] = useMemo(() => {
    const rowData = [...data?.rows];

    if (filteredColumn && rowData) {
      return rowData.sort((a, b) => {
        const valueA = a.columns.find((column) => column.id === filteredColumn.id);
        const valueB = b.columns.find((column) => column.id === filteredColumn.id);

        if (filteredColumn.descending) {
          if (typeof valueA?.filterValue === 'number' && typeof valueB?.filterValue === 'number') {
            return valueA?.filterValue < valueB?.filterValue ? -1 : 1;
          } else {
            return String(valueA?.filterValue).toLowerCase() < String(valueB?.filterValue).toLowerCase() ? -1 : 1;
          }
        } else {
          if (typeof valueA?.filterValue === 'number' && typeof valueB?.filterValue === 'number') {
            return valueA?.filterValue < valueB?.filterValue ? 1 : -1;
          } else {
            return String(valueA?.filterValue).toLowerCase() < String(valueB?.filterValue).toLowerCase() ? 1 : -1;
          }
        }
      });
    }

    return rowData || [];
  }, [data?.rows, filteredColumn?.descending]);

  //const numberOfColumns = data?.headers.length;

  const numberOfColumns = useMemo(() => {
    const filteredHeaders = data?.headers.filter((header) => {
      return !header.hideOnMobile;
    });

    return filteredHeaders.length;
  }, [data?.headers]);

  const columnWidth = useMemo(() => {
    return 100 / numberOfColumns;
  }, [numberOfColumns]);

  useEffect(() => {
    if (defaultFilter) {
      setFilteredColumn(defaultFilter);
    }
  }, [defaultFilter]);

  const headerClick = (header: IBaseTableHeader) => {
    if (filteredColumn?.descending) {
      setFilteredColumn({ id: header.columnId, descending: false });
    } else {
      setFilteredColumn({ id: header.columnId, descending: true });
    }
  };

  const expandRow = (row: IBaseTableRow) => {
    if (onExpanded) {
      onExpanded(row);
    }
  };

  const closeRow = (row: IBaseTableRow) => {
    if (onClosed) {
      onClosed(row);
    }
  };

  return (
    <div ref={ref} className="base-table cont-dep">
      <div className="base-table-headers">
        {data?.headers.map((header: IBaseTableHeader, index) => (
          <div
            key={`header_${header.id}`}
            className={`base-table-header ${header.hideOnMobile ? 'no-mobile' : ''}`}
            style={{ width: `${columnWidth}%`, maxWidth: header.maxWidth ? `${header.maxWidth}px` : '' }}
          >
            <button
              onClick={() => headerClick(header)}
              tabIndex={0}
              className={`${header.columnId === filteredColumn?.id ? 'selected' : ''}`}
              style={{ display: 'inline-block' }}
            >
              {header.headerData}
            </button>
          </div>
        ))}
      </div>
      <div className="base-table-wrapper">
        {rows.map((row: IBaseTableRow, index) => (
          <TableRow
            key={`row_${row.id}`}
            row={row}
            numberOfColumns={numberOfColumns}
            onExpanded={() => expandRow(row)}
            onClosed={() => closeRow(row)}
          />
        ))}
        {!rows.length && !hideLoader ? (
          <div className="skeleton-loader">
            <div className="skeleton-box row-skeleton"></div>
            <div className="skeleton-box row-skeleton"></div>
            <div className="skeleton-box row-skeleton"></div>
            <div className="skeleton-box row-skeleton"></div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default BaseTable;
